/**
 * Static Asset Loading Manager
 * Coordinates static asset loading with existing DataSource loading states
 */

import { makeObservable, observable, computed, action } from 'mobx';
import cachePerformanceMonitor from '../services/CachePerformanceMonitor';

export interface StaticLoadingProgress {
  criticalAssetsLoaded: boolean;
  totalAssetsLoaded: number;
  totalAssetsCount: number;
  estimatedTimeRemaining: number;
  loadingPhase: 'initializing' | 'loading-critical' | 'loading-additional' | 'complete';
}

export interface AssetLoadingState {
  url: string;
  type: 'html' | 'css' | 'js' | 'json' | 'font' | 'image';
  status: 'pending' | 'loading' | 'loaded' | 'error';
  loadTime?: number;
  size?: number;
  fromCache: boolean;
}

class StaticAssetLoadingManager {
  assets: Map<string, AssetLoadingState> = new Map();
  criticalAssetsLoaded = false;
  loadingPhase: StaticLoadingProgress['loadingPhase'] = 'initializing';
  startTime = Date.now();

  constructor() {
    makeObservable(this, {
      assets: observable,
      criticalAssetsLoaded: observable,
      loadingPhase: observable,
      startTime: observable,
      trackAssetLoading: action,
      markAssetLoaded: action,
      markAssetError: action,
      markCriticalAssetsComplete: action,
      markLoadingComplete: action,
      reset: action,
      loadingProgress: computed,
      cacheEfficiency: computed,
      averageLoadTime: computed,
      networkBytesSaved: computed
    });
    this.setupPerformanceObserver();
  }

  trackAssetLoading(assetType: string, url: string): void {
    const asset: AssetLoadingState = {
      url,
      type: assetType as AssetLoadingState['type'],
      status: 'loading',
      fromCache: false
    };

    this.assets.set(url, asset);
    
    if (this.loadingPhase === 'initializing') {
      this.loadingPhase = 'loading-critical';
    }
  }

  markAssetLoaded(url: string, loadTime: number, fromCache: boolean, size?: number): void {
    const asset = this.assets.get(url);
    if (asset) {
      asset.status = 'loaded';
      asset.loadTime = loadTime;
      asset.fromCache = fromCache;
      asset.size = size;
      
      // Track in performance monitor
      if (fromCache) {
        cachePerformanceMonitor.trackCacheHit(url, loadTime, size);
      } else {
        cachePerformanceMonitor.trackCacheMiss(url, 'not-cached', loadTime);
      }
    }
  }

  markAssetError(url: string, error: string): void {
    const asset = this.assets.get(url);
    if (asset) {
      asset.status = 'error';
      cachePerformanceMonitor.trackCacheMiss(url, error);
    }
  }

  markCriticalAssetsComplete(): void {
    this.criticalAssetsLoaded = true;
    this.loadingPhase = 'loading-additional';
    
    console.log('[STATIC-LOADING] Critical assets loaded in', Date.now() - this.startTime, 'ms');
  }

  markLoadingComplete(): void {
    this.loadingPhase = 'complete';
    
    const totalTime = Date.now() - this.startTime;
    console.log('[STATIC-LOADING] All assets loaded in', totalTime, 'ms');
    
    // Log final metrics
    const metrics = cachePerformanceMonitor.getMetrics();
    console.log('[STATIC-LOADING] Final cache metrics:', metrics);
  }

  get loadingProgress(): StaticLoadingProgress {
    const loadedAssets = Array.from(this.assets.values()).filter(asset => asset.status === 'loaded');
    const totalAssets = this.assets.size;
    
    // Estimate remaining time based on current loading speed
    const elapsedTime = Date.now() - this.startTime;
    const loadingRate = loadedAssets.length / Math.max(elapsedTime, 1);
    const remainingAssets = totalAssets - loadedAssets.length;
    const estimatedTimeRemaining = remainingAssets > 0 ? remainingAssets / loadingRate : 0;

    return {
      criticalAssetsLoaded: this.criticalAssetsLoaded,
      totalAssetsLoaded: loadedAssets.length,
      totalAssetsCount: totalAssets,
      estimatedTimeRemaining: Math.round(estimatedTimeRemaining),
      loadingPhase: this.loadingPhase
    };
  }

  get cacheEfficiency(): number {
    const assets = Array.from(this.assets.values()).filter(asset => asset.status === 'loaded');
    if (assets.length === 0) return 0;
    
    const cachedAssets = assets.filter(asset => asset.fromCache);
    return (cachedAssets.length / assets.length) * 100;
  }

  get averageLoadTime(): number {
    const loadedAssets = Array.from(this.assets.values())
      .filter(asset => asset.status === 'loaded' && asset.loadTime);
    
    if (loadedAssets.length === 0) return 0;
    
    const totalTime = loadedAssets.reduce((sum, asset) => sum + (asset.loadTime || 0), 0);
    return Math.round(totalTime / loadedAssets.length);
  }

  get networkBytesSaved(): number {
    const cachedAssets = Array.from(this.assets.values())
      .filter(asset => asset.status === 'loaded' && asset.fromCache && asset.size);
    
    return cachedAssets.reduce((sum, asset) => sum + (asset.size || 0), 0);
  }

  /**
   * Get assets by type
   */
  getAssetsByType(type: AssetLoadingState['type']): AssetLoadingState[] {
    return Array.from(this.assets.values()).filter(asset => asset.type === type);
  }

  /**
   * Get failed assets
   */
  getFailedAssets(): AssetLoadingState[] {
    return Array.from(this.assets.values()).filter(asset => asset.status === 'error');
  }

  /**
   * Reset loading state
   */
  reset(): void {
    this.assets.clear();
    this.criticalAssetsLoaded = false;
    this.loadingPhase = 'initializing';
    this.startTime = Date.now();
  }

  /**
   * Setup performance observer to automatically track resource loading
   */
  private setupPerformanceObserver(): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'resource') {
              this.handleResourceTiming(entry as PerformanceResourceTiming);
            }
          }
        });
        
        observer.observe({ entryTypes: ['resource'] });
      } catch (error) {
        console.warn('[STATIC-LOADING] PerformanceObserver not supported:', error);
      }
    }
  }

  /**
   * Handle resource timing entries
   */
  private handleResourceTiming(entry: PerformanceResourceTiming): void {
    const url = entry.name;
    
    // Only track static assets
    if (!this.isStaticAsset(url)) return;
    
    const loadTime = Math.round(entry.responseEnd - entry.startTime);
    const fromCache = entry.transferSize === 0 && entry.decodedBodySize > 0;
    const size = entry.transferSize || entry.decodedBodySize;
    
    // Determine asset type from URL
    const assetType = this.getAssetTypeFromUrl(url);
    
    // Track or update asset
    if (!this.assets.has(url)) {
      this.trackAssetLoading(assetType, url);
    }
    
    this.markAssetLoaded(url, loadTime, fromCache, size);
  }

  /**
   * Check if URL is a static asset
   */
  private isStaticAsset(url: string): boolean {
    return /\.(js|css|json|woff2?|eot|ttf|otf|png|jpe?g|svg|gif|ico)$/i.test(url) ||
           url.includes('/assets/') ||
           url.includes('/app/');
  }

  /**
   * Get asset type from URL
   */
  private getAssetTypeFromUrl(url: string): string {
    if (url.endsWith('.html')) return 'html';
    if (/\.(css)$/i.test(url)) return 'css';
    if (/\.(js)$/i.test(url)) return 'js';
    if (/\.(json)$/i.test(url)) return 'json';
    if (/\.(woff2?|eot|ttf|otf)$/i.test(url)) return 'font';
    if (/\.(png|jpe?g|svg|gif|ico)$/i.test(url)) return 'image';
    return 'js'; // default
  }
}

// Export singleton instance
export const staticAssetLoadingManager = new StaticAssetLoadingManager();
export default staticAssetLoadingManager;